import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  PenLine,
  Activity,
  Layers,
  Ellipsis,
  Clock3,
  Bell,
  Cog,
  LogOut,
  Blocks,
  Keyboard, Gauge } from 'lucide-react';
import { useNetState } from '../pwa/net.js';
import { useReports, dismissReport, dismissAllReports, reporter } from '../pwa/report.js';
import { useUpdateReady, applyUpdate } from '../pwa/register.js';
import { openStream, onWireState, type WireState } from '../stream.js';
import { api } from '../api.js';
import { useSession } from '../session.js';
import { useIsDesktop } from '../ui/useIsDesktop.js';
import { Sheet, clock } from '../ui/kit.js';
import { APP_TITLE, screenTitleFor } from './screenTitles.js';

// One shell, two shapes. Under 900px: bottom tabs, the phone. At 900px and
// up: a persistent sidebar and the full route list, the desk. The board
// stream keeps a red dot honest on Jobs (needs_input / red) without polling.
const TABS = [
  { path: '/', label: 'Compose', icon: PenLine },
  { path: '/jobs', label: 'Jobs', icon: Activity },
  { path: '/releases', label: 'Releases', icon: Layers },
  { path: '/more', label: 'More', icon: Ellipsis },
];

const SIDE = [
  { path: '/', label: 'Compose', icon: PenLine, key: 'c' },
  { path: '/jobs', label: 'Jobs', icon: Activity, key: 'j' },
  { path: '/releases', label: 'Releases', icon: Layers, key: 'r' },
  { path: '/capacity', label: 'Capacity', icon: Gauge, key: 'y' },
  { path: '/history', label: 'History', icon: Clock3, key: 'h' },
  { path: '/notifications', label: 'Notifications', icon: Bell, key: 'n' },
  { path: '/blocks', label: 'Blocks', icon: Blocks, key: 'b' },
  { path: '/settings', label: 'Settings', icon: Cog, key: 's' },
];

const SHORTCUTS: { keys: string; what: string }[] = [
  { keys: 'n', what: 'New job. Opens Compose and focuses the prompt' },
  { keys: 'g then j / r / h / s / b', what: 'Go to Jobs, Releases, History, Settings, Blocks' },
  { keys: 'j / k', what: 'Move through the jobs list' },
  { keys: 'Enter', what: 'Open the selected job' },
  { keys: '⌘ Enter', what: 'Send. The prompt in Compose, or a message to a running session' },
  { keys: 's', what: 'Ship the job you are looking at (asks first)' },
  { keys: 'Esc', what: 'Close a sheet or the full-screen log' },
  { keys: '?', what: 'This list' },
];

function typingTarget(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
}

export function OpsShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const net = useNetState();
  const reports = useReports();
  const updateReady = useUpdateReady();
  const { refresh } = useSession();
  // One shape at a time in the DOM: the other shape's controls must not
  // exist at 0×0 for a screen reader or the layout lint to trip over.
  const desktop = useIsDesktop();
  const [attention, setAttention] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  // V6 §A: the banner is the LIVE CHANNEL's state and nothing else.
  // `live` renders nothing (the pattern), `reconnecting` a quiet chip,
  // `offline` the banner — and offline now means every stream has been
  // down for twenty seconds, not that one API read came off the service
  // worker's cache. That second condition is what made the app say "No
  // signal" all week on a phone with working data: NetworkFirst falls
  // back to cache whenever the network is slower than its timeout, and
  // the fetch layer latched on the first such response and never cleared.
  // Staleness is still shown, but as what it is: an "as of" time.
  const [wire, setWire] = useState<WireState>('live');
  useEffect(() => onWireState(setWire), []);

  /* The browser tab names the screen.
     It read "Meridian Ops" on all thirteen addresses (Q-0500, Q-0709), on
     the one surface whose whole job is watching several things at once: the
     board, a job's log, the candidates and the ceremony were four identical
     tabs. The app's own name stays, second, for somebody with the Console
     open beside it. */
  useEffect(() => {
    const screen = screenTitleFor(location.pathname);
    document.title = screen ? `${screen} \u00b7 ${APP_TITLE}` : APP_TITLE;
  }, [location.pathname]);

  useEffect(() => {
    const check = () =>
      api
        .get<{ jobs: { phase: string }[] }>('/api/jobs')
        .then((r) => setAttention(r.jobs.some((j) => ['needs_input', 'red', 'green_held', 'limit', 'auth', 'api_error', 'crashed', 'budget'].includes(j.phase))))
        .catch(reporter('the board'));
    void check();
    const stream = openStream('/api/events', {
      job: () => void check(),
      notification: () => void check(),
    });
    return () => stream.close();
  }, []);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.data?.kind === 'navigate' && typeof e.data.url === 'string') {
        const url = new URL(e.data.url, window.location.origin);
        navigate(url.pathname + url.search);
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  }, [navigate]);

  // Global keys: n (compose), ? (help), g-then-letter chords. Screens own
  // their local keys (j/k, s, ⌘Enter). Nothing fires while typing.
  useEffect(() => {
    let chord: 'g' | null = null;
    let chordAt = 0;
    const onKey = (e: KeyboardEvent) => {
      if (typingTarget(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (chord === 'g' && Date.now() - chordAt < 900) {
        const hit = SIDE.find((s) => s.key === e.key);
        chord = null;
        if (hit) {
          e.preventDefault();
          navigate(hit.path);
          return;
        }
      }
      if (e.key === 'g') {
        chord = 'g';
        chordAt = Date.now();
        return;
      }
      chord = null;
      if (e.key === 'n') {
        e.preventDefault();
        navigate('/');
        window.setTimeout(() => {
          document.querySelector<HTMLTextAreaElement>('.ops-textarea--prompt')?.focus();
        }, 50);
      } else if (e.key === '?') {
        e.preventDefault();
        setShowKeys((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const current = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="ops-shell ops-shell--desk">
      {desktop ? (
        <aside className="ops-side">
        <div className="ops-side__brand">
          <img src="/icons/ops-192.png" alt="" />
          <b>Meridian Ops</b>
        </div>
        {SIDE.map((t) => (
          <button
            key={t.path}
            type="button"
            className="ops-side__item"
            aria-current={current(t.path)}
            onClick={() => navigate(t.path)}
          >
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <t.icon />
              {t.path === '/jobs' && attention ? <span className="ops-side__dot" /> : null}
            </span>
            {t.label}
          </button>
        ))}
        <div className="ops-side__foot">
          <button type="button" className="ops-side__item" onClick={() => setShowKeys(true)}>
            <Keyboard />
            Shortcuts
          </button>
          <button
            type="button"
            className="ops-side__item"
            onClick={() => void api.post('/api/auth/logout').then(refresh).catch(reporter('signing out'))}
          >
            <LogOut />
            Sign out
          </button>
          <p className="ops-side__hint">
            <kbd>n</kbd> new job · <kbd>?</kbd> shortcuts
          </p>
        </div>
        </aside>
      ) : null}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {updateReady ? (
          <div className="ops-banner ops-banner--update">
            A newer Ops is ready. Your draft is saved.
            <button type="button" onClick={applyUpdate}>
              Reload
            </button>
          </div>
        ) : wire === 'offline' ? (
          <div className="ops-banner" data-testid="wire-state" data-state="offline">
            The live channel has been silent for 45 seconds.{net.lastFetchedAt ? ` Showing what was last read, as of ${clock(net.lastFetchedAt)}.` : ''}
          </div>
        ) : wire === 'reconnecting' ? (
          <div className="ops-banner ops-banner--wait" data-testid="wire-state" data-state="reconnecting">
            Reconnecting…
          </div>
        ) : null}
        <main className="ops-shell__body">
          <Outlet />
        </main>
        {/* Background reads that failed. Inline errors live next to their
            own control; this is for everything a screen fetches on your
            behalf, which used to fail in complete silence (V6 section J). */}
        {reports.length ? (
          <div className="ops-reports" role="status" data-testid="reports">
            {reports.map((r) => (
              <div className="ops-reports__item" key={r.id}>
                <span>
                  <b>{r.what}</b> did not load. {r.message}
                  {r.count > 1 ? ` (${r.count} times)` : ''}
                </span>
                <button type="button" aria-label="Dismiss" onClick={() => dismissReport(r.id)}>
                  ×
                </button>
              </div>
            ))}
            {reports.length > 1 ? (
              <button type="button" className="ops-reports__all" onClick={dismissAllReports}>
                Dismiss all
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {!desktop ? (
        <nav className="ops-tabbar">
          {TABS.map((t) => (
            <button
              key={t.path}
              type="button"
              className="ops-tabbar__tab"
              aria-current={current(t.path)}
              onClick={() => navigate(t.path)}
            >
              <span style={{ position: 'relative' }}>
                <t.icon />
                {t.path === '/jobs' && attention ? <span className="ops-tabbar__dot" /> : null}
              </span>
              {t.label}
            </button>
          ))}
        </nav>
      ) : null}
      {showKeys ? (
        <Sheet title="Keyboard shortcuts" onClose={() => setShowKeys(false)}>
          <div className="ops-rows">
            {SHORTCUTS.map((s) => (
              <div key={s.keys} className="ops-row" style={{ minHeight: 44 }}>
                <div className="ops-row__main">
                  <div className="ops-row__sub">{s.what}</div>
                </div>
                <div className="ops-row__end">
                  <kbd
                    style={{
                      fontFamily: 'var(--ops-mono)',
                      fontSize: 11.5,
                      background: 'var(--ops-surface-2)',
                      border: '1px solid var(--ops-line)',
                      borderRadius: 5,
                      padding: '2px 7px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.keys}
                  </kbd>
                </div>
              </div>
            ))}
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
